'use client';

/**
 * 발굴 색인에서 **못 찾은 계정만** 실시간 창구로 메운다 (`/api/influencer/profiles`).
 *
 * 색인 조회(`docs.byIds`)는 공짜라 먼저 하고, 거기서 얼굴이 안 나온 계정만 이 창구에 묻는다.
 * 창구는 색인에 **문서가 아예 없는 계정**도 그 자리에서 받아 채운다(실측: 브랜드 허브 상위 20계정 중 색인 1).
 *
 * ## 🚨 유료 창구다 — 아껴 쓰는 규칙이 이 파일의 본체다
 * ```
 *   화면에 보이는 계정만        부르는 쪽이 이미 «불러온 글»만 넘긴다
 *   못 찾은 것만                색인에 얼굴이 있으면 안 묻는다
 *   한 번에 20건                백엔드 상한과 같다
 *   채운 것은 다시 안 묻는다    `filled` 에 쌓아 두고 목록이 늘어도 제외한다
 *   그만두라는 답은 존중한다    `unsupported`·`unavailable`·`deferred` 는 `done` 으로 닫는다
 *   여섯 번까지만 조른다        3초 × 6 ≈ 18초 · 그 뒤 pending 은 닫는다(뒤에서 계속 채워져 다음 방문에 온다)
 * ```
 * ⚠️ 하루 상한은 **전체 한 덩어리**다(워크스페이스별이 아니다) — 넘으면 `deferred` 가 온다.
 * ⚠️ 답을 기다리며 화면을 막지 않는다 — 못 찾으면 부르는 쪽이 머리글자로 떨어뜨린다.
 */

import type { ProfileLookupResult } from 'src/types/influencer/profile-lookup';

import { useRef, useMemo, useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { fetchProfileLookups } from 'src/actions/influencer-profile-lookup';

import {
  PROFILE_LOOKUP_POLL_MS,
  PROFILE_LOOKUP_POLL_MAX,
} from 'src/types/influencer/profile-lookup';

import { profileKey } from '../../../utils/search-all-media-cards';
import { toLookupMissing, lookupResultToProfile } from '../../../utils/account-profile-overlay';

import type { AccountProfile } from '../../../utils/search-all-media-cards';

const EMPTY: Map<string, AccountProfile> = new Map();
const EMPTY_KEYS: ReadonlySet<string> = new Set();

/** 이 답이 오면 그 계정은 더 묻지 않는다 */
const CLOSING: ProfileLookupResult['status'][] = ['unsupported', 'unavailable', 'deferred'];

/**
 * @param accounts 화면에 뜬 계정 — 채널 + 아이디
 * @param ledger 색인에서 이미 찾은 것(여기 얼굴이 있으면 안 묻는다)
 * @returns 창구가 채운 것만 — 부르는 쪽이 색인 위에 덮는다
 */
export function useAccountProfileLookup(
  accounts: { channel: string; account: string }[],
  ledger: Map<string, AccountProfile>,
): Map<string, AccountProfile> {
  const [filled, setFilled] = useState<Map<string, AccountProfile>>(EMPTY);
  /** 그만 묻기로 한 계정 — 창구가 닫았거나 여섯 번을 다 쓴 것 */
  const [done, setDone] = useState<ReadonlySet<string>>(EMPTY_KEYS);

  const missing = useMemo(
    () => toLookupMissing(accounts, ledger, (key) => filled.has(key) || done.has(key)),
    [accounts, ledger, filled, done],
  );

  const askKey = missing.map((row) => profileKey(row.channel, row.account)).join('|');
  /** 이 묶음을 몇 번 졸랐나 — 묶음이 바뀌면 다시 센다 */
  const tries = useRef(0);
  useEffect(() => { tries.current = 0; }, [askKey]);

  const query = useQuery({
    queryKey: ['influencer', 'profile-lookup', askKey],
    enabled: missing.length > 0,
    placeholderData: keepPreviousData,
    // 캐시로 되돌아오면 조르기가 멈춘 채 굳는다 — 늘 새로 묻는다
    gcTime: 0,
    staleTime: 0,
    queryFn: () => {
      tries.current += 1;
      return fetchProfileLookups(missing);
    },
    refetchInterval: (q) => {
      const rows = (q.state.data ?? []) as ProfileLookupResult[];
      const waiting = rows.some((row) => row.status === 'pending');
      return waiting && tries.current < PROFILE_LOOKUP_POLL_MAX ? PROFILE_LOOKUP_POLL_MS : false;
    },
  });

  useEffect(() => {
    const rows = query.data;
    if (!rows || rows.length === 0) return;
    const exhausted = tries.current >= PROFILE_LOOKUP_POLL_MAX;

    setFilled((prev) => {
      const next = new Map(prev);
      let changed = false;
      rows.forEach((row) => {
        const profile = lookupResultToProfile(row);
        if (!profile) return;
        const key = profileKey(row.channel, row.account);
        const was = prev.get(key);
        if (was && was.avatar === profile.avatar && was.followers === profile.followers) return;
        next.set(key, profile);
        changed = true;
      });
      return changed ? next : prev;
    });

    setDone((prev) => {
      const next = new Set(prev);
      let changed = false;
      rows.forEach((row) => {
        const closing = CLOSING.includes(row.status) || (exhausted && row.status === 'pending');
        if (!closing) return;
        const key = profileKey(row.channel, row.account);
        if (next.has(key)) return;
        next.add(key);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [query.data]);

  return filled;
}
